import React from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Link, navigate } from 'gatsby-plugin-intl'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'
import { selectTrackingDetails } from 'src/features/tracking/duck/selectors'
import routes from 'src/utils/routes'
import { createOfferDetailsLink } from '../features/dashboard/duck/consts'
import { Dialog, ActionBar, CancelButton, ConfirmButton } from './Dialog'

const ModalContent = styled.div`
  line-height: 1.6;

  p {
    margin: 0 0 20px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const OfferLink = styled(Link)`
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  text-decoration: none;
  font-weight: normal;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.easternBlue.darken(0.1).hex()};
  }
`

const AcceptWithTrackingModal = ({
  open,
  close,
  title,
  offerReferenceNumber,
  trackingOfferId,
  intl,
  confirmText,
  cancelText,
  onConfirm
}) => {
  const trackingDetails = useSelector(selectTrackingDetails)
  const trackingId =
    trackingOfferId &&
    trackingDetails?.id &&
    String(trackingDetails?.offer?.id) === String(trackingOfferId)
      ? trackingDetails.id
      : null
  const isTrackingPending = Boolean(trackingOfferId) && !trackingId

  const rawMessage = intl.formatMessage(
    { id: 'myOffers.acceptWithTrackingModalDescription' },
    { offerId: offerReferenceNumber }
  )

  const parts = rawMessage.split('<p>')
  const firstPart = parts[0]
  const secondPart = parts[1]?.replace('</p>', '') || ''

  const firstPartWithLink = firstPart.replace(
    offerReferenceNumber,
    'OFFER_LINK_PLACEHOLDER'
  )
  const beforeOffer = firstPartWithLink.split('OFFER_LINK_PLACEHOLDER')[0]
  const afterOffer = firstPartWithLink.split('OFFER_LINK_PLACEHOLDER')[1]

  const handleConfirm = () => {
    if (isTrackingPending) {
      return
    }

    let confirmAction = null

    if (onConfirm) {
      confirmAction = () => onConfirm({ trackingId })
    } else if (trackingId) {
      confirmAction = () =>
        navigate(
          `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.TRACKING_EDIT}&id=${trackingId}`
        )
    }

    if (!confirmAction) {
      return
    }

    const promise = confirmAction()
    if (promise) {
      promise.finally(() => close && close())
    } else {
      close && close()
    }
  }

  return (
    <Dialog open={open} onClose={close} title={title}>
      <ModalContent>
        <p>
          {beforeOffer}
          <OfferLink to={createOfferDetailsLink(offerReferenceNumber)}>
            {offerReferenceNumber}
          </OfferLink>
          {afterOffer}
        </p>
        {secondPart && <p>{secondPart}</p>}
      </ModalContent>
      <ActionBar>
        <CancelButton onClick={close}>{cancelText}</CancelButton>
        <ConfirmButton disabled={isTrackingPending} onClick={handleConfirm}>
          {isTrackingPending ? (
            <>
              <Loader size={16} />
              {intl.formatMessage({ id: 'common.loading' }) || 'Loading...'}
            </>
          ) : (
            confirmText
          )}
        </ConfirmButton>
      </ActionBar>
    </Dialog>
  )
}

export default AcceptWithTrackingModal

const Loader = styled(CircularProgress)`
  width: 16px !important;
  height: 16px !important;
  margin-right: 0.5rem;
  color: currentColor !important;
`
